import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { AuthProvider, useAuth } from './context/AuthContext';
import Layout from './components/Layout/Layout';
import RoleRoute from './components/RoleRoute';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard/Dashboard';
import Choferes from './pages/Choferes/Choferes';
import Buses from './pages/Buses/Buses';
import Usuarios from './pages/Usuarios/Usuarios';
import Tarifas from './pages/Tarifas/Tarifas';
import Cajas from './pages/Cajas/Cajas';
import Reportes from './pages/Reportes/Reportes';
import Perfil from './pages/Perfil/Perfil';

// Sistema de Redondos
import Itinerarios from './pages/Itinerarios/Itinerarios';
import Redondos from './pages/Redondos/Redondos';
import Gastos from './pages/Gastos/Gastos';

// ========================================
// NUEVO - Módulo de Chofer
// Agregado: 2026-01-03
// ========================================
import DashboardChofer from './pages/chofer/DashboardChofer';
import NuevaBoletaChofer from './pages/chofer/NuevaBoletaChofer';
import NuevoGastoChofer from './pages/chofer/NuevoGastoChofer';
// import DetalleRedondoChofer from './pages/chofer/DetalleRedondoChofer'; // Por crear

/**
 * App principal con rutas protegidas por roles
 * Path: backoffice-web/src/App.js
 * Última modificación: 2026-01-03 19:30
 * Dev: cpalacios01@gmail.com | +595994648273
 * 
 * ROLES DEL SISTEMA:
 * - role_id = 1: Administrador (acceso total)
 * - role_id = 2: Conductor/Chofer (módulo específico /chofer/*)
 * - role_id = 3: Rendidor (rendiciones y redondos)
 */

const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
  },
});

// Componente de ruta protegida
function PrivateRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return <div>Cargando...</div>;
  }
  
  return isAuthenticated ? children : <Navigate to="/login" />;
}

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <Router future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true
        }}>
          <Routes>
            {/* Ruta pública */}
            <Route path="/login" element={<Login />} />
            
            {/* Rutas protegidas */}
            <Route path="/" element={
              <PrivateRoute>
                <Layout />
              </PrivateRoute>
            }>
              {/* ========================================
                  DASHBOARD PRINCIPAL
                  Redirige automáticamente según rol
                  ======================================== */}
              <Route index element={
                <RoleRoute requiredPath="/">
                  <Dashboard />
                </RoleRoute>
              } />
              
              {/* ========================================
                  MÓDULO DE CHOFER (role_id === 2)
                  NUEVO - Agregado 2026-01-03
                  ======================================== */}
              
              {/* Dashboard del Chofer */}
              <Route path="chofer/dashboard" element={
                <RoleRoute requiredPath="/chofer/dashboard">
                  <DashboardChofer />
                </RoleRoute>
              } />
              
              {/* Nueva Boleta */}
              <Route path="chofer/nueva-boleta" element={
                <RoleRoute requiredPath="/chofer/nueva-boleta">
                  <NuevaBoletaChofer />
                </RoleRoute>
              } />
              
              {/* Nuevo Gasto */}
              <Route path="chofer/nuevo-gasto" element={
                <RoleRoute requiredPath="/chofer/nuevo-gasto">
                  <NuevoGastoChofer />
                </RoleRoute>
              } />
              
              {/* Detalle del Redondo - Comentado hasta crear el componente
              <Route path="chofer/detalle-redondo" element={
                <RoleRoute requiredPath="/chofer/detalle-redondo">
                  <DetalleRedondoChofer />
                </RoleRoute>
              } />
              */}
              
              {/* ========================================
                  SISTEMA DE REDONDOS
                  ======================================== */}
              
              {/* Redondos - Administrador y Rendidor */}
              <Route path="redondos" element={
                <RoleRoute requiredPath="/redondos">
                  <Redondos />
                </RoleRoute>
              } />
              
              {/* Itinerarios - Solo Administrador */}
              <Route path="itinerarios" element={
                <RoleRoute requiredPath="/itinerarios">
                  <Itinerarios />
                </RoleRoute>
              } />
              
              {/* Gastos - Todos los roles */}
              <Route path="gastos" element={
                <RoleRoute requiredPath="/gastos">
                  <Gastos />
                </RoleRoute>
              } />
              
              {/* ========================================
                  MÓDULOS EXISTENTES
                  ======================================== */}
              
              {/* Choferes - Solo Administrador */}
              <Route path="choferes" element={
                <RoleRoute requiredPath="/choferes">
                  <Choferes />
                </RoleRoute>
              } />
              
              {/* Buses - Solo Administrador */}
              <Route path="buses" element={
                <RoleRoute requiredPath="/buses">
                  <Buses />
                </RoleRoute>
              } />
              
              {/* Usuarios - Solo Administrador */}
              <Route path="usuarios" element={
                <RoleRoute requiredPath="/usuarios">
                  <Usuarios />
                </RoleRoute>
              } />
              
              {/* Tarifas - Solo Administrador */}
              <Route path="tarifas" element={
                <RoleRoute requiredPath="/tarifas">
                  <Tarifas />
                </RoleRoute>
              } />
              
              {/* Cajas - Todos (pero con diferentes vistas) */}
              <Route path="cajas" element={
                <RoleRoute requiredPath="/cajas">
                  <Cajas />
                </RoleRoute>
              } />
              
              {/* Reportes - Administrador y Rendidor */}
              <Route path="reportes" element={
                <RoleRoute requiredPath="/reportes">
                  <Reportes />
                </RoleRoute>
              } />
              
              {/* Perfil - Todos */}
              <Route path="perfil" element={
                <RoleRoute requiredPath="/perfil">
                  <Perfil />
                </RoleRoute>
              } />
            </Route>
            
            {/* Ruta por defecto */}
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;

// cpalacios01@gmail.com | +595994648273
